/**
 * Budget Service
 * Handles all budget management API operations
 * 
 * @module services/budgetService
 * @requires services/api/BaseService
 */

import { BaseService } from './api';

/**
 * Budget Service Class
 * Provides methods for budget allocation, variance analysis, forecasting, and approvals
 * 
 * @class BudgetService
 * @extends BaseService
 * 
 * @example
 * import budgetService from './services/budgetService';
 * 
 * // Get all budgets
 * const budgets = await budgetService.getAll({ fiscalYear: 2024 });
 * 
 * // Create budget
 * await budgetService.create({
 *   department: 'Sales',
 *   amount: 50000000,
 *   fiscalYear: 2024
 * });
 */
class BudgetService extends BaseService {
  constructor() {
    super('/budgets');
  }

  /**
   * Get all budgets with pagination and filters
   * 
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=20] - Items per page
   * @param {string} [params.department] - Filter by department
   * @param {string} [params.category] - Filter by category
   * @param {number} [params.fiscalYear] - Filter by fiscal year
   * @param {string} [params.status] - Filter by status (draft, submitted, approved, rejected, active, closed)
   * @param {string} [params.search] - Search query
   * @param {string} [params.sortBy] - Sort field
   * @param {string} [params.sortOrder] - Sort order (asc, desc)
   * @returns {Promise<{data: Array, total: number, page: number, limit: number}>}
   */
  async getAll(params = {}) {
    return this.get('', { params });
  }

  /**
   * Get budget by ID
   * 
   * @param {string} budgetId - Budget ID
   * @returns {Promise<Object>} Budget details
   */
  async getById(budgetId) {
    return this.get(`/${budgetId}`);
  }

  /**
   * Create new budget
   * 
   * @param {Object} budgetData - Budget data
   * @param {string} budgetData.name - Budget name
   * @param {string} budgetData.department - Department
   * @param {string} budgetData.category - Category
   * @param {number} budgetData.amount - Budget amount
   * @param {number} budgetData.fiscalYear - Fiscal year
   * @param {string} budgetData.period - Period (annual, quarterly, monthly)
   * @param {string} [budgetData.description] - Description
   * @param {Object} [budgetData.breakdown] - Budget breakdown
   * @returns {Promise<Object>} Created budget
   */
  async create(budgetData) {
    return this.post('', budgetData);
  }

  /**
   * Update budget
   * 
   * @param {string} budgetId - Budget ID
   * @param {Object} budgetData - Updated budget data
   * @returns {Promise<Object>} Updated budget
   */
  async update(budgetId, budgetData) {
    return this.put(`/${budgetId}`, budgetData);
  }

  /**
   * Delete budget
   * 
   * @param {string} budgetId - Budget ID
   * @returns {Promise<void>}
   */
  async delete(budgetId) {
    return this.delete(`/${budgetId}`);
  }

  /**
   * Search budgets with cancellation
   * 
   * @param {string} query - Search query
   * @param {Object} [options={}] - Search options
   * @param {number} [options.fiscalYear] - Filter by fiscal year
   * @param {number} [options.limit=10] - Max results
   * @returns {Promise<Array>} Matching budgets
   */
  async search(query, options = {}) {
    return this.getWithCancel('/search', 'budget-search', {
      params: { q: query, ...options }
    });
  }

  /**
   * Get budgets by department
   * 
   * @param {string} department - Department name
   * @param {Object} [params={}] - Additional parameters
   * @returns {Promise<Array>} Department budgets
   */
  async getByDepartment(department, params = {}) {
    return this.get('/by-department', { params: { department, ...params } });
  }

  /**
   * Get budgets by fiscal year
   * 
   * @param {number} fiscalYear - Fiscal year
   * @param {Object} [params={}] - Additional parameters
   * @returns {Promise<Array>} Fiscal year budgets
   */
  async getByFiscalYear(fiscalYear, params = {}) {
    return this.get('/by-fiscal-year', { params: { fiscalYear, ...params } });
  }

  /**
   * Allocate budget
   * 
   * @param {string} budgetId - Budget ID
   * @param {Object} allocationData - Allocation data
   * @param {number} allocationData.amount - Amount to allocate
   * @param {string} allocationData.category - Allocation category
   * @param {string} [allocationData.description] - Description
   * @returns {Promise<Object>} Allocation record
   */
  async allocate(budgetId, allocationData) {
    return this.post(`/${budgetId}/allocate`, allocationData);
  }

  /**
   * Get budget allocations
   * 
   * @param {string} budgetId - Budget ID
   * @param {Object} [params={}] - Query parameters
   * @returns {Promise<Array>} Budget allocations
   */
  async getAllocations(budgetId, params = {}) {
    return this.get(`/${budgetId}/allocations`, { params });
  }

  /**
   * Get budget variance analysis
   * 
   * @param {string} budgetId - Budget ID
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period] - Analysis period
   * @returns {Promise<Object>} Variance analysis data
   */
  async getVarianceAnalysis(budgetId, params = {}) {
    return this.get(`/${budgetId}/variance`, { params });
  }

  /**
   * Get variance analysis for all budgets
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.department] - Filter by department
   * @param {number} [params.fiscalYear] - Filter by fiscal year
   * @param {string} [params.threshold] - Variance threshold filter
   * @returns {Promise<Array>} Variance data for all budgets
   */
  async getAllVariances(params = {}) {
    return this.get('/variance-analysis', { params });
  }

  /**
   * Get budget utilization
   * 
   * @param {string} budgetId - Budget ID
   * @returns {Promise<Object>} Utilization data
   */
  async getUtilization(budgetId) {
    return this.get(`/${budgetId}/utilization`);
  }

  /**
   * Get budget forecast
   * 
   * @param {string} budgetId - Budget ID
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.forecastMonths=6] - Months to forecast
   * @param {string} [params.method='trend'] - Forecast method
   * @returns {Promise<Object>} Forecast data
   */
  async getForecast(budgetId, params = {}) {
    return this.get(`/${budgetId}/forecast`, { params });
  }

  /**
   * Submit budget for approval
   * 
   * @param {string} budgetId - Budget ID
   * @param {Object} [submissionData={}] - Submission data
   * @param {string} [submissionData.notes] - Submission notes
   * @returns {Promise<Object>} Updated budget
   */
  async submit(budgetId, submissionData = {}) {
    return this.post(`/${budgetId}/submit`, submissionData);
  }

  /**
   * Approve budget
   * 
   * @param {string} budgetId - Budget ID
   * @param {Object} [approvalData={}] - Approval data
   * @param {string} [approvalData.comments] - Approval comments
   * @returns {Promise<Object>} Updated budget
   */
  async approve(budgetId, approvalData = {}) {
    return this.post(`/${budgetId}/approve`, approvalData);
  }

  /**
   * Reject budget
   * 
   * @param {string} budgetId - Budget ID
   * @param {Object} rejectionData - Rejection data
   * @param {string} rejectionData.reason - Rejection reason
   * @param {string} [rejectionData.comments] - Additional comments
   * @returns {Promise<Object>} Updated budget
   */
  async reject(budgetId, rejectionData) {
    return this.post(`/${budgetId}/reject`, rejectionData);
  }

  /**
   * Get budget approval history
   * 
   * @param {string} budgetId - Budget ID
   * @returns {Promise<Array>} Approval history
   */
  async getApprovalHistory(budgetId) {
    return this.get(`/${budgetId}/approval-history`);
  }

  /**
   * Get pending approvals
   * 
   * @param {Object} [params={}] - Query parameters
   * @returns {Promise<Array>} Budgets pending approval
   */
  async getPendingApprovals(params = {}) {
    return this.get('/pending-approvals', { params });
  }

  /**
   * Revise budget
   * 
   * @param {string} budgetId - Budget ID
   * @param {Object} revisionData - Revision data
   * @param {number} revisionData.amount - New budget amount
   * @param {string} revisionData.reason - Revision reason
   * @param {string} [revisionData.notes] - Additional notes
   * @returns {Promise<Object>} Revised budget
   */
  async revise(budgetId, revisionData) {
    return this.post(`/${budgetId}/revise`, revisionData);
  }

  /**
   * Get budget revision history
   * 
   * @param {string} budgetId - Budget ID
   * @returns {Promise<Array>} Revision history
   */
  async getRevisionHistory(budgetId) {
    return this.get(`/${budgetId}/revisions`);
  }

  /**
   * Get budget alerts
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.severity] - Filter by severity (critical, warning)
   * @param {string} [params.type] - Filter by type (overspend, underspend, nearLimit)
   * @param {boolean} [params.unread] - Only unread alerts
   * @returns {Promise<Array>} Budget alerts
   */
  async getAlerts(params = {}) {
    return this.get('/alerts', { params });
  }

  /**
   * Get budget analytics
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.fiscalYear] - Filter by fiscal year
   * @param {string} [params.department] - Filter by department
   * @param {string} [params.groupBy] - Group by field
   * @returns {Promise<Object>} Budget analytics
   */
  async getAnalytics(params = {}) {
    return this.get('/analytics', { params });
  }

  /**
   * Get budget summary dashboard
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.fiscalYear] - Fiscal year
   * @returns {Promise<Object>} Dashboard summary
   */
  async getDashboardSummary(params = {}) {
    return this.get('/dashboard/summary', { params });
  }

  /**
   * Compare budgets
   * 
   * @param {Object} comparisonData - Comparison data
   * @param {Array<string>} comparisonData.budgetIds - Budget IDs to compare
   * @param {string} [comparisonData.metric] - Metric to compare
   * @returns {Promise<Object>} Comparison data
   */
  async compare(comparisonData) {
    return this.post('/compare', comparisonData);
  }

  /**
   * Export budget data
   * 
   * @param {Object} [params={}] - Export parameters
   * @param {string} [params.format='xlsx'] - Export format (csv, xlsx, pdf)
   * @param {Array<string>} [params.budgetIds] - Specific budgets to export
   * @param {Object} [params.filters] - Filters to apply
   * @returns {Promise<Blob>} Export file
   */
  async export(params = {}) {
    return this.get('/export', {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Import budgets from file
   * 
   * @param {File} file - CSV or Excel file
   * @param {Object} [options={}] - Import options
   * @param {number} options.fiscalYear - Fiscal year for budgets
   * @param {boolean} [options.updateExisting=false] - Update existing budgets
   * @param {boolean} [options.skipErrors=true] - Skip rows with errors
   * @returns {Promise<{imported: number, updated: number, failed: number, errors: Array}>}
   */
  async import(file, options) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    
    return this.post('/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Get budget statistics
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.fiscalYear] - Fiscal year
   * @returns {Promise<Object>} Budget statistics
   */
  async getStatistics(params = {}) {
    return this.get('/statistics', { params });
  }
}

// Export singleton instance
const budgetService = new BudgetService();
export default budgetService;
